import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { getStoresPaginatedApi, StorePublic } from "../../store/storeApi";
import {
  ActivationCode,
  createKioskDeviceApi,
  KIOSK_STATUS_LABELS,
  KioskDevice,
  listKioskDevicesApi,
  newActivationCodeApi,
  revokeKioskDeviceApi,
} from "../api/kioskApi";
import { QUIOSQUE_PAGE } from "../../quiosque/routes";
import ReasonModal from "./ReasonModal";
import "../pages/Timekeeping.css";

const STATUS_BADGE: Record<KioskDevice["status"], string> = {
  ATIVO: "dpBadge--green",
  AGUARDANDO_ATIVACAO: "dpBadge--orange",
  REVOGADO: "dpBadge--gray",
};

const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" }) : "—";

/**
 * Aba "Aparelhos do quiosque" em Cadastros (ADR 0031). Cada loja com aparelho
 * ativo marca o ponto só no quiosque; o portal deixa de marcar lá. O código de
 * ativação aparece uma vez só, na hora de gerar.
 *
 * Com `storeId` (aba Quiosque da ficha da loja), mostra só os aparelhos dela e o
 * cadastro já vem com a loja.
 */
export default function KioskDevicesSettings({ token, storeId }: { token: string; storeId?: string }) {
  const [items, setItems] = useState<KioskDevice[]>([]);
  const [stores, setStores] = useState<StorePublic[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ storeId: "", name: "" });
  const [code, setCode] = useState<(ActivationCode & { deviceName: string }) | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<KioskDevice | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await listKioskDevicesApi(token);
      setItems(storeId ? all.filter((d) => d.store.id === storeId) : all);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os aparelhos.");
    } finally {
      setLoading(false);
    }
  }, [token, storeId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (storeId) return;
    getStoresPaginatedApi(token, { all: true })
      .then((res) => setStores(res.items ?? []))
      .catch(() => setStores([]));
  }, [token, storeId]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const targetStoreId = storeId ?? form.storeId;
    if (!targetStoreId) return toast.error("Escolha a loja.");
    if (form.name.trim().length < 2) return toast.error("Dê um nome ao aparelho.");
    setSaving(true);
    try {
      const created = await createKioskDeviceApi(token, { storeId: targetStoreId, name: form.name.trim() });
      setModal(false);
      setForm({ storeId: "", name: "" });
      setCode({ ...created, deviceName: created.name });
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao cadastrar o aparelho.");
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async (device: KioskDevice) => {
    setSaving(true);
    try {
      const created = await newActivationCodeApi(token, device.id);
      setCode({ ...created, deviceName: device.name });
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao gerar o código.");
    } finally {
      setSaving(false);
    }
  };

  const kioskUrl = `${window.location.origin}${QUIOSQUE_PAGE()}`;

  return (
    <>
      <div className="dpControls" style={{ flexWrap: "wrap", gap: 8 }}>
        <button className="dpBtn dpBtn--primary" onClick={() => setModal(true)} disabled={saving}>
          Novo aparelho
        </button>
      </div>
      <p className="dpFormHint" style={{ marginBottom: 12 }}>
        O celular fixo da loja onde o colaborador marca o ponto pelo rosto. Na loja com aparelho ativo, o ponto deixa de ser
        marcado pelo app do colaborador. Abra <strong>{kioskUrl}</strong> no aparelho e digite o código de ativação.
      </p>

      {loading ? (
        <div className="dpEmpty">Carregando...</div>
      ) : (
        <div className="dpTableWrapper">
          <table className="dpTable">
            <thead>
              <tr>
                <th>Loja</th>
                <th>Aparelho</th>
                <th>Situação</th>
                <th>Último contato</th>
                <th style={{ width: 190 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="dpEmpty">
                    Nenhum aparelho cadastrado. Enquanto a loja não tiver aparelho, o ponto continua pelo app do colaborador.
                  </td>
                </tr>
              ) : (
                items.map((d) => (
                  <tr key={d.id}>
                    <td>{d.store.name}</td>
                    <td>{d.name}</td>
                    <td>
                      <span className={`dpBadge ${STATUS_BADGE[d.status]}`}>{KIOSK_STATUS_LABELS[d.status]}</span>
                      {d.status === "REVOGADO" && d.revokedReason ? <div className="dpMuted">{d.revokedReason}</div> : null}
                    </td>
                    <td>{formatDateTime(d.lastSeenAt)}</td>
                    <td>
                      {d.status !== "REVOGADO" ? (
                        <div className="dpTable__actions">
                          <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => regenerate(d)} disabled={saving}>
                            {d.status === "ATIVO" ? "Reinstalar" : "Novo código"}
                          </button>
                          <button className="dpBtn dpBtn--danger dpBtn--small" onClick={() => setRevokeTarget(d)} disabled={saving}>
                            Revogar
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal ? (
        <div className="dpModalOverlay">
          <form className="dpModal" onSubmit={save}>
            <header className="dpModal__header">
              <h2 className="dpModal__title">Novo aparelho</h2>
              <button type="button" className="dpModal__close" onClick={() => setModal(false)}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <div className="dpFormGrid">
                {storeId ? null : (
                <div className="dpFormGroup">
                  <label htmlFor="kioskStore">Loja *</label>
                  <select id="kioskStore" className="dpSelect" value={form.storeId} onChange={(e) => setForm({ ...form, storeId: e.target.value })}>
                    <option value="">Escolha</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nomeFantasia}
                      </option>
                    ))}
                  </select>
                </div>
                )}
                <div className="dpFormGroup">
                  <label htmlFor="kioskName">Nome do aparelho *</label>
                  <input
                    id="kioskName"
                    className="dpInput"
                    maxLength={80}
                    placeholder="Ex.: Celular da entrada"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <footer className="dpModal__footer">
              <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(false)} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                {saving ? "Salvando..." : "Cadastrar e gerar código"}
              </button>
            </footer>
          </form>
        </div>
      ) : null}

      {code ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">Código de ativação — {code.deviceName}</h2>
              <button type="button" className="dpModal__close" onClick={() => setCode(null)}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <p className="dpKioskCode">{code.activationCode}</p>
              <p className="dpNotice">
                Anote agora: o código não aparece de novo. Vale {code.expiresInMinutes} minutos e uma ativação só. No aparelho, abra{" "}
                <strong>{kioskUrl}</strong> e digite o código.
              </p>
            </div>
            <footer className="dpModal__footer">
              <button type="button" className="dpBtn dpBtn--primary" onClick={() => setCode(null)}>
                Pronto
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {revokeTarget ? (
        <ReasonModal
          title={`Revogar ${revokeTarget.name}?`}
          description={
            <>
              O aparelho deixa de marcar o ponto na hora. Para voltar a usar, cadastre um aparelho novo. Se for o único aparelho de{" "}
              <strong>{revokeTarget.store.name}</strong>, o ponto da loja volta para o app do colaborador.
            </>
          }
          confirmLabel="Revogar"
          onClose={() => setRevokeTarget(null)}
          onConfirm={async (reason) => {
            await revokeKioskDeviceApi(token, revokeTarget.id, reason);
            toast.success("Aparelho revogado.");
            setRevokeTarget(null);
            await load();
          }}
        />
      ) : null}
    </>
  );
}
